/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
const JSG = require('../../JSG');

/**
 * Class to handle a sheet range.
 *
 * @constructor
 * @class CellRange
 */
module.exports = class CellRange {
	constructor(sheet, x1, y1, x2, y2, x1R, y1R, x2R, y2R) {
		this._sheet = sheet;

		this.set(x1, y1, x2, y2);

		// relative/absolute flag
		this._x1R = x1R || true;
		this._x2R = x2R || true;
		this._y1R = y1R || true;
		this._y2R = y2R || true;
	}

	copy() {
		const copy = new CellRange();

		copy.setTo(this);

		return copy;
	}

	setTo(range) {
		this._sheet = range._sheet;

		this._x1 = range._x1;
		this._x2 = range._x2;
		this._y1 = range._y1;
		this._y2 = range._y2;
		this._x1R = range._x1R;
		this._x2R = range._x2R;
		this._y1R = range._y1R;
		this._y2R = range._y2R;
		return this;
	}

	toObject() {
		const obj = {};

		obj.id = this._sheet.id;

		obj.x1 = this._x1;
		obj.x2 = this._x2;
		obj.y1 = this._y1;
		obj.y2 = this._y2;
		obj.x1R = this._x1R;
		obj.x2R = this._x2R;
		obj.y1R = this._y1R;
		obj.y2R = this._y2R;

		return obj;
	}

	isEqualTo(other) {
		return this._x1 === other._x1 && this._x2 === other._x2 && this._y1 === other._y1 && this._y2 === other._y2;
	}

	isSingleCell() {
		return Math.abs(this._x1 - this._x2) === 0 && Math.abs(this._y1 - this._y2) === 0;
	}

	isValid() {
		return (
			this._sheet !== undefined &&
			this._x1 !== undefined &&
			this._y1 !== undefined &&
			this._x2 !== undefined &&
			this._y2 !== undefined
		);
	}

	set(x1, y1, x2, y2) {
		this._x1 = x1;
		if (x2 === undefined) {
			this._x2 = x1;
		} else {
			this._x2 = x2;
		}
		this._y1 = y1;
		if (y2 === undefined) {
			this._y2 = y1;
		} else {
			this._y2 = y2;
		}
		this.sort();
		return this;
	}

	sort() {
		if (this._x1 > this._x2) {
			const x1 = this._x1;
			const x1R = this._x1R;
			this._x1 = this._x2;
			this._x1R = this._x2R;
			this._x2 = x1;
			this._x2R = x1R;
		}

		if (this._y1 > this._y2) {
			const y1 = this._y1;
			const y1R = this._y1R;
			this._y1 = this._y2;
			this._y1R = this._y2R;
			this._y2 = y1;
			this._y2R = y1R;
		}
	}

	contains(cell) {
		return cell.x >= this._x1 && cell.x <= this._x2 && cell.y >= this._y1 && cell.y <= this._y2;
	}

	intersects(range) {
		return this._x1 <= range._x2 && range._x1 <= this._x2 && this._y1 <= range._y2 && range._y1 <= this._y2;
	}

	get sheet() {
		return this._sheet;
	}

	getSheet() {
		return this._sheet;
	}

	getX1() {
		return this._x1;
	}

	setX1(x1) {
		this._x1 = x1;
		this.sort();
		return this;
	}

	getY1() {
		return this._y1;
	}

	setY1(y1) {
		this._y1 = y1;
		this.sort();
		return this;
	}

	get width() {
		return this._x2 - this._x1 + 1;
	}

	get height() {
		return this._y2 - this._y1 + 1;
	}

	getWidth() {
		return this._x2 - this._x1 + 1;
	}

	getHeight() {
		return this._y2 - this._y1 + 1;
	}

	setWidth() {
		this._x2 = this._x1;
		return this;
	}

	setHeight() {
		this._y2 = this._y1;
		return this;
	}

	getX2() {
		return this._x2;
	}

	setX2(x2) {
		this._x2 = x2;
		this.sort();
		return this;
	}

	getY2() {
		return this._y2;
	}

	setY2(y2) {
		this._y2 = y2;
		this.sort();
		return this;
	}

	isSheetRange() {
		return (
			this.width === this._sheet.maxColumns && this.height === this._sheet.maxRows
		);
	}

	isRowRange() {
		return this.width === this._sheet.maxColumns;
	}

	isColumnRange() {
		return this.height === this._sheet.maxRows;
	}

	applyFormat() {}

	containsValues(rangeToIgnore) {
		const dataProvider = this._sheet.cells;
		let values = false;

		this.enumerateCells(false, (pos) => {
			if (
				pos.x < rangeToIgnore.getX1() ||
				pos.x > rangeToIgnore.getX2() ||
				pos.y < rangeToIgnore.getY1() ||
				pos.y > rangeToIgnore.getY2()
			) {
				const cell = dataProvider.get(pos);
				if (cell && cell.hasContent() && cell.getValue() !== '') {
					values = true;
				}
			}
		});

		return values;
	}

	enumerateShifted(callback) {
		const data = this._sheet.cells;
		const cInit = this._sheet.colOffset;
		let i;
		let j;

		/* eslint-disable no-mixed-operators */
		for (i = 0; i < this.width; i += 1) {
			for (j = 0; j < this.height; j += 1) {
				const cell = data.getRC(this._x1 + i - cInit, this._y1 + j);
				callback(cell, i, j);
			}
		}
	}

	enumerateCells(skipRowsOrColumns, callback) {
		let j;
		let k;
		const cell = JSG.ptCache.get();

		if (
			!skipRowsOrColumns ||
			(this.height !== this._sheet.maxRows && this.width !== this._sheet.maxColumns)
		) {
			for (j = this._x1; j <= this._x2; j += 1) {
				for (k = this._y1; k <= this._y2; k += 1) {
					cell.set(j, k);
					callback.call(this, cell);
				}
			}
		}

		JSG.ptCache.release(cell);
	}

	enumerateColumns(callback) {
		let j;

		for (j = this._x1; j <= this._x2; j += 1) {
			if (this.height === this._sheet.maxRows) {
				callback.call(this, j);
			}
		}
	}

	enumerateRows(callback) {
		let j;

		for (j = this._y1; j <= this._y2; j += 1) {
			if (this.width === this._sheet.maxColumns) {
				callback.call(this, j);
			}
		}
	}

	getColumnString(columnNumber) {
		return CellRange.getColumnStringFromIndex(columnNumber, this._sheet);
	};

	/**
	 * Saves this Range instance.
	 *
	 * @method save
	 * @param {Writer} writer Writer to use for streaming.
	 */
	save(writer) {
		writer.writeStartElement('range');
		writer.writeEndElement();
	}

	toString(params) {
		let str = '';

		const all = this.isRowRange() && this.isColumnRange();

		if (all || !this.isRowRange()) {
			if (this._x1R === false) {
				str += '$';
			}

			str += this.getColumnString(this._x1);
		}

		if (all || !this.isColumnRange()) {
			if (this._y1R === false) {
				str += '$';
			}
			str += this._y1 + 1;
		}

		if (this.width > 1 || this.height > 1) {
			str += ':';
			if (all || !this.isRowRange()) {
				if (this._x2R === false) {
					str += '$';
				}

				str += this.getColumnString(this._x2);
			}

			if (all || !this.isColumnRange()) {
				if (this._y2R === false) {
					str += '$';
				}
				str += this._y2 + 1;
			}
		}

		if (params && params.rangeColors) {
			// mark range for edit control
			str = `~span${str}~/span`;
		}

		// if (params && params.item) {
		// 	console.log('getPropertyString with Item');
		// }

		if (params && (params.forceName || params.sheet !== this._sheet)) {
			return `${this._sheet.name}!${str}`;
		}

		return str;
	}

	shiftToSheet() {
		this._x1 += this._sheet.colOffset;
		this._x2 += this._sheet.colOffset;

		return this;
	}

	shiftFromSheet() {
		this._x1 -= this._sheet.colOffset;
		this._x2 -= this._sheet.colOffset;

		return this;
	}

	static parse(rangeText, sheet, allowExternalRef = true, noLimit = false) {
		if (rangeText === undefined) {
			return undefined;
		}

		const range = new CellRange(sheet);

		const cutIndex = rangeText.indexOf('!');
		if (cutIndex !== -1) {
			if (allowExternalRef === false) {
				return undefined;
			}
			const name = rangeText.substring(0, cutIndex);
			const namedItem = JSG.SheetModel.findSheetModel(name);
			if (namedItem === undefined) {
				return undefined;
			}
			range._sheet = namedItem;
			rangeText = rangeText.substring(cutIndex + 1);
		}

		const cells = rangeText.split(':');

		const start = CellRange.refToRC(cells[0], range._sheet, noLimit);
		if (start === undefined) {
			return undefined;
		}
		if (start.row === undefined) {
			if (cells.length === 1) {
				return undefined;
			}
			start.row = 0;
			start.rowRel = true;
		}
		if (start.column === undefined) {
			if (cells.length === 1) {
				return undefined;
			}
			start.column = range._sheet.colOffset;
			start.colRel = true;
		}

		range._x1 = start.column;
		range._y1 = start.row;
		range._x1R = start.colRel;
		range._y1R = start.rowRel;

		if (cells.length > 1) {
			const end = CellRange.refToRC(cells[1], range._sheet, noLimit);
			if (end === undefined) {
				return undefined;
			}
			if (end.row === undefined) {
				end.row = range._sheet.maxRows - 1;
				end.rowRel = true;
			}
			if (end.column === undefined) {
				end.column = range._sheet.maxColumns + range._sheet.colOffset - 1;
				end.colRel = true;
			}
			range._x2 = end.column;
			range._y2 = end.row;
			range._x2R = end.colRel;
			range._y2R = end.rowRel;
		} else {
			range._x2 = start.column;
			range._y2 = start.row;
			range._x2R = start.colRel;
			range._y2R = start.rowRel;
		}

		range.sort();

		return range;
	}

	static getColumnFromString(reference) {
		if (reference === 'COMMENT') return 0;
		if (reference === 'IF') return 1;

		let colVal = 0;
		for (let j = 0; j < reference.length; j += 1) {
			colVal =
				26 * colVal +
				reference
					.charAt(j)
					.toUpperCase()
					.charCodeAt(0) -
				65 +
				1;
		}

		return colVal;
	}

	static getColumnStringFromIndex(index, sheet) {
		let columnName = '';
		if (index < 0) {
			columnName = sheet.columns.getSectionTitle(index - sheet.colOffset);
		} else {
			let dividend = index + 1;
			let modulo;

			while (dividend > 0) {
				modulo = (dividend - 1) % 26;
				columnName = String.fromCharCode(65 + modulo).toString() + columnName;
				dividend = parseInt((dividend - modulo) / 26, 0);
			}
		}
		return columnName;
	}

	static refToRC(reference, sheet, noLimit = false) {
		let i = 0;
		let j;
		let colVal = 0;
		let col;
		let absCnt = 0;

		if (sheet === undefined) {
			return undefined;
		}

		let char;

		while (i < reference.length) {
			char = reference.charAt(i);
			if (char >= '0' && char <= '9') {
				break;
			}
			if ((char < 'A' || char > 'Z') && char !== '$') {
				return undefined;
			}
			absCnt += char === '$' ? 1 : 0;
			i += 1;
		}

		char = reference.charAt(i - 1);

		const endOfCol = char === '$' ? i - 1 : i;
		const colRel = reference[0] !== '$';
		const rowRel = char !== '$';

		// skip $ of col, if any
		j = reference[0] === '$' ? 1 : 0;

		// j now points to first character of column address
		// check column names for negative columns
		const colref = reference.slice(j, endOfCol);
		if (colref === 'IF') {
			col = 0;
		} else if (colref === 'COMMENT') {
			col = -1;
		} else {
			if (i > 2 + absCnt) {
				return undefined;
			}
			if (i !== 0) {
				for (j; j < endOfCol; j += 1) {
					colVal =
						26 * colVal +
						reference
							.charAt(j)
							.toUpperCase()
							.charCodeAt(0) -
						65 +
						1; // 65 -> 'A'
				}

				col = colVal > 0 ? colVal : 1;
			}
		}

		if (i === reference.length) {
			return {
				row: undefined,
				rowRel: undefined,
				column: col - 1,
				colRel
			};
		}

		const rowVal = Number(reference.substring(i));

		if (isNaN(rowVal) || (noLimit === false && rowVal - 1 > sheet.maxRows)) {
			return undefined;
		}

		return {
			row: rowVal - 1,
			rowRel,
			column: col === undefined ? undefined : col - 1,
			colRel: col === undefined ? undefined : colRel
		};
	}
};
